import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { i18n } from "../../translate/i18n";
import { Slider, Stack, IconButton, Alert, Paper, Grid, Box } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, Shuffle as ShuffleIcon } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

const pathLabels = ["A", "B", "C", "D"];

const FlowBuilderRandomizerModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const [activeModal, setActiveModal] = useState(false);
  const [paths, setPaths] = useState([]);

  const totalPercent = useMemo(() => {
    return paths.reduce((sum, p) => sum + p.percent, 0);
  }, [paths]);

  useEffect(() => {
    if (open) {
      const initialPaths = data?.data?.paths || [
        { path: "A", percent: 100 },
      ];
      setPaths(initialPaths);
      setActiveModal(true);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handlePathValueChange = (index, newValue) => {
    const newPaths = [...paths];
    newPaths[index].percent = Math.min(Math.max(newValue, 0), 100);
    setPaths(newPaths);
  };

  const addPath = () => {
    if (paths.length < 4) {
      const newPaths = [...paths, { path: pathLabels[paths.length], percent: 0 }];
      setPaths(newPaths);
    }
  };

  const removePath = (index) => {
    if (paths.length > 1) {
      const newPaths = paths.filter((_, i) => i !== index);
      setPaths(newPaths);
    }
  };

  const autoBalancePercentages = () => {
    if (paths.length === 0) return;
    const basePercentage = Math.floor(100 / paths.length);
    const remainder = 100 % paths.length;
    const newPaths = paths.map((path, index) => ({
      ...path,
      percent: basePercentage + (index < remainder ? 1 : 0),
    }));
    setPaths(newPaths);
  };

  const handleSave = () => {
    if (totalPercent !== 100) {
      toast.error("A soma das porcentagens deve ser 100%.");
      return;
    }

    const dataToSave = { paths };

    if (open === "edit") {
      onUpdate({ ...data, data: dataToSave });
    } else {
      onSave(dataToSave);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>
          {open === "create"
            ? "Adicionar um randomizador ao fluxo"
            : "Editar randomizador"}
        </DialogTitle>
        <DialogContent dividers>
          <Box className={classes.header}>
            <Typography variant="h6">
              Caminhos de Distribuição
            </Typography>
            <Button
              variant="outlined"
              onClick={autoBalancePercentages}
              startIcon={<ShuffleIcon />}
              disabled={paths.length === 0}
            >
              Distribuir %
            </Button>
          </Box>

          {paths.map((path, index) => (
            <Paper key={index} className={classes.paper} elevation={2}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <Typography variant="h6">{path.path}</Typography>
                </Grid>
                <Grid item xs>
                  <Slider
                    value={path.percent}
                    onChange={(e, newValue) => handlePathValueChange(index, newValue)}
                    aria-labelledby="continuous-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    min={0}
                    max={100}
                  />
                </Grid>
                <Grid item>
                  <Typography fontWeight="bold">{path.percent}%</Typography>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => removePath(index)} size="small" disabled={paths.length <= 1}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {paths.length < 4 && (
            <Button
              onClick={addPath}
              startIcon={<AddIcon />}
              variant="outlined"
              fullWidth
            >
              Adicionar Caminho
            </Button>
          )}

          <Box mt={2}>
            <Alert
              severity={totalPercent !== 100 ? "error" : "success"}
            >
              Total: {totalPercent}% {totalPercent !== 100 && "(a soma deve ser 100%)"}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
            disabled={totalPercent !== 100}
          >
            {open === "create" ? "Adicionar" : "Editar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderRandomizerModal;


